import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, Building2, FileText, AlertTriangle, Shield, Users, 
  ChevronDown, ChevronUp, Phone, Mail, User, UserCheck, HardHat,
  Activity, RefreshCw, Eye
} from 'lucide-react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

// Body silhouette image component with front and back view for injury visualization
const BodySilhouette = ({ injuries = [], size = 'small' }) => {
  const dimensions = size === 'large' 
    ? { width: 300, height: 270 } 
    : { width: 160, height: 144 };
  
  const getInjuryColor = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('first') || t === 'firstaid') return '#22c55e'; // Green
    if (t.includes('medical')) return '#eab308'; // Yellow
    if (t.includes('recordable')) return '#ef4444'; // Red
    return '#6b7280'; // Gray
  };
  
  return (
    <div className="relative" style={{ width: dimensions.width, height: dimensions.height }}>
      {/* Professional silhouette image - front and back view */}
      <img 
        src="https://customer-assets.emergentagent.com/job_formbuilder-7/artifacts/dj7vgm3e_injury%20silouette.png" 
        alt="Human body silhouette - front and back"
        className="w-full h-full object-contain pointer-events-none"
        style={{ 
          filter: 'contrast(1.1)',
          mixBlendMode: 'multiply'
        }}
        draggable={false}
      />
      
      {/* Injury dots */}
      {injuries.map((injury, index) => {
        // Parse position - injuries have x,y as percentages or pixel values
        let x = parseFloat(injury.x) || 50;
        let y = parseFloat(injury.y) || 50;
        
        // If x/y contain '%' or are already percentages (0-100), use as is
        // If they look like pixel values for a ~500px container, convert
        if (typeof injury.x === 'string' && injury.x.includes('%')) {
          x = parseFloat(injury.x);
        } else if (x > 100) {
          // Looks like pixel value, convert to percentage based on ~500px container
          x = (x / 500) * 100;
        }
        
        if (typeof injury.y === 'string' && injury.y.includes('%')) {
          y = parseFloat(injury.y);
        } else if (y > 100) {
          y = (y / 450) * 100;
        }
        
        return (
          <div
            key={index}
            className="absolute rounded-full animate-pulse"
            style={{
              left: `${x}%`,
              top: `${y}%`,
              width: size === 'large' ? 14 : 10,
              height: size === 'large' ? 14 : 10,
              backgroundColor: getInjuryColor(injury.type),
              transform: 'translate(-50%, -50%)',
              boxShadow: `0 0 ${size === 'large' ? 8 : 5}px ${getInjuryColor(injury.type)}`,
              border: '2px solid white'
            }}
            title={`${injury.bodyPart || 'Unknown'} - ${injury.type || 'Unknown'}`}
          />
        );
      })}
    </div>
  );
};

// Legend for body map colors
const BodyMapLegend = ({ compact = false }) => (
  <div className={`flex ${compact ? 'gap-3 text-xs' : 'gap-4 text-sm'}`}>
    <div className="flex items-center gap-1">
      <div className="w-3 h-3 rounded-full bg-green-500" />
      <span>First Aid</span>
    </div>
    <div className="flex items-center gap-1">
      <div className="w-3 h-3 rounded-full bg-yellow-500" />
      <span>Medical</span>
    </div>
    <div className="flex items-center gap-1">
      <div className="w-3 h-3 rounded-full bg-red-500" />
      <span>Recordable</span>
    </div>
  </div>
);

// Subcontractor Tile Component
const SubcontractorTile = ({ sub, onViewDetails }) => {
  const [expanded, setExpanded] = useState(false);
  
  const { analytics, contacts, bodyMap } = sub;
  
  // Calculate compliance status based on data
  const getComplianceStatus = () => {
    if (analytics.totalIncidents === 0 && analytics.avgAuditScore >= 80) {
      return { color: 'bg-green-500', text: 'Good Standing', icon: '✅' };
    }
    if (analytics.totalIncidents > 2 || analytics.avgAuditScore < 70) {
      return { color: 'bg-red-500', text: 'Needs Attention', icon: '⚠️' };
    }
    return { color: 'bg-yellow-500', text: 'Monitoring', icon: '👀' };
  };
  
  const status = getComplianceStatus();
  
  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-lg">{sub.subCompanyName}</CardTitle>
          </div>
          <Badge className={`${status.color} text-white`}>
            {status.icon} {status.text}
          </Badge>
        </div>
        {sub.divisionName && (
          <p className="text-xs text-gray-500 mt-1">Division: {sub.divisionName}</p>
        )}
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Analytics Summary */}
        <div className="grid grid-cols-4 gap-2 text-center">
          <div className="p-2 bg-blue-50 rounded">
            <FileText className="w-4 h-4 text-blue-600 mx-auto mb-1" />
            <p className="text-lg font-bold text-blue-700">{analytics.totalJsas}</p>
            <p className="text-xs text-gray-500">JSAs</p>
          </div>
          <div className="p-2 bg-red-50 rounded">
            <AlertTriangle className="w-4 h-4 text-red-600 mx-auto mb-1" />
            <p className="text-lg font-bold text-red-700">{analytics.totalIncidents}</p>
            <p className="text-xs text-gray-500">Incidents</p>
          </div>
          <div className="p-2 bg-green-50 rounded">
            <Shield className="w-4 h-4 text-green-600 mx-auto mb-1" />
            <p className="text-lg font-bold text-green-700">{analytics.totalAudits}</p>
            <p className="text-xs text-gray-500">Audits</p>
          </div>
          <div className="p-2 bg-purple-50 rounded">
            <Activity className="w-4 h-4 text-purple-600 mx-auto mb-1" />
            <p className="text-lg font-bold text-purple-700">
              {analytics.avgAuditScore > 0 ? `${analytics.avgAuditScore}%` : '-'}
            </p>
            <p className="text-xs text-gray-500">Avg Score</p>
          </div>
        </div>
        
        {/* Body Map Mini */}
        <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
          <div className="flex-shrink-0">
            <BodySilhouette injuries={bodyMap.injuries} size="small" />
          </div>
          <div className="flex-1 ml-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Injury Summary</p>
            <div className="space-y-1 text-xs">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  First Aid
                </span>
                <span className="font-semibold">{bodyMap.byType.firstAid}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-yellow-500" />
                  Medical
                </span>
                <span className="font-semibold">{bodyMap.byType.medical}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  Recordable
                </span>
                <span className="font-semibold">{bodyMap.byType.recordable}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Admin Contact (always visible) */}
        {contacts.admin && (
          <div className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg">
            <User className="w-4 h-4 text-blue-600" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{contacts.admin.name}</p>
              <p className="text-xs text-gray-500 truncate">{contacts.admin.email}</p>
            </div>
          </div>
        )}
        
        {/* Expand Button */}
        <Button 
          variant="ghost" 
          className="w-full justify-between"
          onClick={() => setExpanded(!expanded)}
        >
          <span>{expanded ? 'Hide Details' : 'Show All Contacts'}</span>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>
        
        {/* Expanded Content */}
        {expanded && (
          <div className="space-y-3 pt-2 border-t">
            <p className="text-sm font-semibold text-gray-700">All Contacts</p>
            
            {/* Admin */}
            {contacts.admin && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium">Admin</span>
                </div>
                <p className="text-sm font-semibold">{contacts.admin.name}</p>
                <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                  <Mail className="w-3 h-3" />
                  <a href={`mailto:${contacts.admin.email}`} className="hover:text-blue-600">
                    {contacts.admin.email}
                  </a>
                </div>
                {contacts.admin.phone && (
                  <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                    <Phone className="w-3 h-3" />
                    <a href={`tel:${contacts.admin.phone}`} className="hover:text-blue-600">
                      {contacts.admin.phone}
                    </a>
                  </div>
                )}
              </div>
            )}
            
            {/* Supervisor */}
            {contacts.supervisor && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <UserCheck className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium">Supervisor</span>
                </div>
                <p className="text-sm font-semibold">{contacts.supervisor.name}</p>
                <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                  <Mail className="w-3 h-3" />
                  <a href={`mailto:${contacts.supervisor.email}`} className="hover:text-blue-600">
                    {contacts.supervisor.email}
                  </a>
                </div>
                {contacts.supervisor.phone && (
                  <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                    <Phone className="w-3 h-3" />
                    <a href={`tel:${contacts.supervisor.phone}`} className="hover:text-blue-600">
                      {contacts.supervisor.phone}
                    </a>
                  </div>
                )}
              </div>
            )}
            
            {/* Safety Officer */}
            {contacts.safetyOfficer && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <HardHat className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium">{contacts.safetyOfficer.role || 'Safety Officer'}</span>
                </div>
                <p className="text-sm font-semibold">{contacts.safetyOfficer.name}</p>
                <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                  <Mail className="w-3 h-3" />
                  <a href={`mailto:${contacts.safetyOfficer.email}`} className="hover:text-blue-600">
                    {contacts.safetyOfficer.email}
                  </a>
                </div>
                {contacts.safetyOfficer.phone && (
                  <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                    <Phone className="w-3 h-3" />
                    <a href={`tel:${contacts.safetyOfficer.phone}`} className="hover:text-blue-600">
                      {contacts.safetyOfficer.phone}
                    </a>
                  </div>
                )}
              </div>
            )}
            
            {!contacts.supervisor && !contacts.safetyOfficer && !contacts.admin && (
              <p className="text-sm text-gray-500 text-center py-2">No contacts available</p>
            )}
            
            {/* View Full Details Button */}
            <Button 
              className="w-full mt-2" 
              variant="outline"
              onClick={() => onViewDetails(sub)}
            >
              <Eye className="w-4 h-4 mr-2" />
              View Full Details
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Dashboard Component
const GCProjectDashboard = ({ user }) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  
  useEffect(() => {
    if (projectId) {
      fetchDashboardData();
    }
  }, [projectId]);
  
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/gc/projects/${projectId}/dashboard-tiles`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  const handleViewSubDetails = (sub) => {
    navigate(`/gc/projects/${projectId}/sub/${sub.subCompanyId}`);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }
  
  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto text-center py-12">
          <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-600">Project not found</h2>
          <Button className="mt-4" onClick={() => navigate('/gc/dashboard')}>
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }
  
  const { projectName, projectLocation, projectOverview, subcontractorTiles } = dashboardData;
  
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/gc/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{projectName}</h1>
              {projectLocation && (
                <p className="text-gray-600">{projectLocation}</p>
              )}
            </div>
          </div>
          <Button variant="outline" onClick={fetchDashboardData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {/* Project Overview Tile (Full Width) */}
        {projectOverview && (
          <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                Project Overview - All Subcontractors Combined
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Left: Stats */}
                <div className="space-y-4">
                  {/* Main Stats Grid */}
                  <div className="grid grid-cols-5 gap-3">
                    <div className="p-4 bg-white rounded-lg shadow-sm text-center">
                      <FileText className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-blue-700">{projectOverview.totalJsas}</p>
                      <p className="text-xs text-gray-500">Total JSAs</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-sm text-center">
                      <AlertTriangle className="w-6 h-6 text-red-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-red-700">{projectOverview.totalIncidents}</p>
                      <p className="text-xs text-gray-500">Incidents</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-sm text-center">
                      <Shield className="w-6 h-6 text-green-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-green-700">{projectOverview.totalAudits}</p>
                      <p className="text-xs text-gray-500">Audits</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-sm text-center">
                      <Activity className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-yellow-700">{projectOverview.totalNearMisses}</p>
                      <p className="text-xs text-gray-500">Near Misses</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-sm text-center">
                      <Shield className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-purple-700">
                        {projectOverview.avgAuditScore > 0 ? `${projectOverview.avgAuditScore}%` : '-'}
                      </p>
                      <p className="text-xs text-gray-500">Avg Score</p>
                    </div>
                  </div>
                  
                  {/* Incidents by Severity */}
                  {Object.keys(projectOverview.incidentsBySeverity).length > 0 && (
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <p className="text-sm font-medium text-gray-700 mb-3">Incidents by Severity</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(projectOverview.incidentsBySeverity).map(([severity, count]) => (
                          <Badge 
                            key={severity}
                            className={
                              severity.toLowerCase() === 'critical' ? 'bg-red-600' :
                              severity.toLowerCase() === 'high' ? 'bg-orange-500' :
                              severity.toLowerCase() === 'medium' ? 'bg-yellow-500' :
                              'bg-green-500'
                            }
                          >
                            {severity}: {count}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Subcontractor Count */}
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                      <Users className="w-8 h-8 text-indigo-600" />
                      <div>
                        <p className="text-2xl font-bold text-indigo-700">{subcontractorTiles.length}</p>
                        <p className="text-sm text-gray-500">Active Subcontractors</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right: Body Map */}
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm font-medium text-gray-700 mb-3">Project Injury Body Map</p>
                  <div className="flex items-center justify-center gap-6">
                    <BodySilhouette injuries={projectOverview.bodyMap.injuries} size="large" />
                    <div className="space-y-3">
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-3xl font-bold text-gray-700">{projectOverview.bodyMap.total}</p>
                        <p className="text-xs text-gray-500">Total Injuries</p>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-4 p-2 bg-green-50 rounded">
                          <span className="flex items-center gap-2 text-sm">
                            <div className="w-3 h-3 rounded-full bg-green-500" />
                            First Aid
                          </span>
                          <span className="font-bold text-green-700">{projectOverview.bodyMap.byType.firstAid}</span>
                        </div>
                        <div className="flex items-center justify-between gap-4 p-2 bg-yellow-50 rounded">
                          <span className="flex items-center gap-2 text-sm">
                            <div className="w-3 h-3 rounded-full bg-yellow-500" />
                            Medical
                          </span>
                          <span className="font-bold text-yellow-700">{projectOverview.bodyMap.byType.medical}</span>
                        </div>
                        <div className="flex items-center justify-between gap-4 p-2 bg-red-50 rounded">
                          <span className="flex items-center gap-2 text-sm">
                            <div className="w-3 h-3 rounded-full bg-red-500" />
                            Recordable
                          </span>
                          <span className="font-bold text-red-700">{projectOverview.bodyMap.byType.recordable}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Subcontractor Tiles Grid */}
        {subcontractorTiles.length > 0 ? (
          <>
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Subcontractor Details ({subcontractorTiles.length})
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {subcontractorTiles.map((sub) => (
                <SubcontractorTile 
                  key={sub.subCompanyId} 
                  sub={sub}
                  onViewDetails={handleViewSubDetails}
                />
              ))}
            </div>
          </>
        ) : (
          <Card className="text-center py-12">
            <CardContent>
              <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-600">No Subcontractors Yet</h3>
              <p className="text-gray-500 mt-2">Invite subcontractors to this project to see their analytics</p>
              <Button className="mt-4" onClick={() => navigate('/gc/dashboard')}>
                Go to GC Dashboard
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default GCProjectDashboard;
